/**
 * The closed vocabulary of `Evidence.kind` (#14854, ADR-0107).
 *
 * `Evidence.kind` is a `String` column (`prisma/schema.prisma`, default
 * `"pulse_text"`), but it is a DISPATCH KEY, not a label: meeting record
 * access, owner-confirmation reads, the decision retype job, meeting prep,
 * classification source defaults and the ActionBoard friction queries all
 * filter on it. A misspelled kind on a write compiles, lands, and is invisible
 * to every one of those readers. This module is the list; the schema below is
 * the gate and the `EvidenceKind` type is the convenience.
 *
 * Two shapes, and the difference is the point:
 *
 *   - SEVENTEEN FLAT KINDS — {@link FLAT_EVIDENCE_KINDS}. One value, one producer.
 *   - FOUR `prefix:member` FAMILIES —
 *       - `communication:` with a CLOSED member set
 *         ({@link COMMUNICATION_EVIDENCE_MEMBERS}): a logged family email or
 *         phone call. Classification keys on the prefix (ADR-0124), meeting
 *         prep on the full values.
 *       - `staff_pulse_friction:` with an OPEN member: a theme slug derived
 *         from free text by `frictionThemeSlug`. The ActionBoard reads it by
 *         prefix only (`startsWith`), so the theme set is data, not code —
 *         closing it here would make every new theme a code change. The member
 *         is constrained to slug SHAPE instead, which is what keeps a write
 *         readable: an empty or mis-cased member is refused.
 *       - `activity:` and `integration:` with CLOSED member sets
 *         ({@link ACTIVITY_EVIDENCE_MEMBERS}, {@link INTEGRATION_EVIDENCE_MEMBERS}):
 *         what the nightly synopsis providers learned about a person
 *         (#15496, ADR-0098 amendment). `activity:<provider>` is one
 *         low-weight count row per provider per night and never links to a
 *         Priority; `integration:<provider>` is a content finding (a shipped
 *         PR, a closed Linear issue, a meeting pattern) that may.
 *
 * CLIENT-SAFE: zod only — no `server-only`, no Prisma. Not to be confused
 * with `src/lib/evidence-kinds.ts`, which (despite its name) holds
 * `Belief.type` values.
 */

import { z } from 'zod'

/** The kinds that are a single value rather than a family. */
export const FLAT_EVIDENCE_KINDS = [
  'pulse_text',
  'notetaker_transcript',
  'owner_confirmation',
  'assessment',
  'blocker',
  'comment',
  'measure_reading',
  'meeting_highlight',
  'decision_leaning',
  'decision_agreed',
  'decision_ratification',
  'question',
  'question_decider',
  'question_parked',
  'question_referred',
  'decision_proposal_triage',
  'provenance',
] as const

export type FlatEvidenceKind = (typeof FLAT_EVIDENCE_KINDS)[number]

/**
 * `Evidence.kind`'s schema default (`prisma/schema.prisma`): captured
 * conversational text. Its twin in the schema must stay equal to it.
 */
export const DEFAULT_EVIDENCE_KIND =
  'pulse_text' as const satisfies FlatEvidenceKind

/**
 * A row the note taker wrote from a transcript (ADR-0104 D5). Not the
 * default: a reader asking for "evidence from a recording" cannot tell it
 * from a reflection under `pulse_text`.
 */
export const NOTETAKER_EVIDENCE_KIND =
  'notetaker_transcript' as const satisfies FlatEvidenceKind

/** A person's confirmation of a note-taker reading (#14763). */
export const OWNER_CONFIRMATION_EVIDENCE_KIND =
  'owner_confirmation' as const satisfies FlatEvidenceKind

/**
 * A person's (or the system's) rating of a node with a reason — the Evidence
 * behind an `assessment` Belief, written only by `createAssessment`
 * (`@/lib/assessments/create-assessment`, #15001, ADR-0126 § 5).
 */
export const ASSESSMENT_EVIDENCE_KIND =
  'assessment' as const satisfies FlatEvidenceKind

/**
 * A problem raised against the priorities it drags on — the Evidence behind
 * a `blocker` Belief, written only by `raiseBlocker`
 * (`@/lib/agenda/blockers`, #15009, ADR-0126 § 6). The legacy-`Issue`
 * backfill finds its own rows again by this kind and the issue's URL.
 */
export const BLOCKER_EVIDENCE_KIND =
  'blocker' as const satisfies FlatEvidenceKind

/**
 * A comment on a graph node, read by the comment extractor (#15190, ADR-0128
 * § 3): the comment's verbatim text, backing a Belief through a `COMMENT`
 * link origin that never grants read. Written only by
 * `@/lib/comments/graph-extraction`; its `commentId` names the comment.
 */
export const COMMENT_EVIDENCE_KIND =
  'comment' as const satisfies FlatEvidenceKind

/**
 * A measure's reading — the Evidence behind a `MeasureReading` satellite
 * (#15016, ADR-0126 § 7), which carries its value, as-of date and measure.
 * Written by the #15016 Measurable backfill (which cited each legacy data
 * point as `/measurables/<id>#data-point-<id>`; #15023's migration repoints
 * PIP baselines through that citation and removed the script with the
 * tables) and by `recordMeasureReadingInTx`; a data sync later writes the
 * same shape.
 */
export const MEASURE_READING_EVIDENCE_KIND =
  'measure_reading' as const satisfies FlatEvidenceKind

/**
 * A meeting highlight (#15408): what a participant typed under an agenda
 * section in the room, verbatim, on the run's `MEETING` pulse and placed by
 * `Evidence.meetingSectionId`. Written only by
 * `src/app/actions/meeting-highlights.ts` (and the migration that moved the
 * `meeting_highlights` rows here, keeping their ids).
 */
export const MEETING_HIGHLIGHT_EVIDENCE_KIND =
  'meeting_highlight' as const satisfies FlatEvidenceKind

/**
 * The three human acts that climb a decision's formality ladder above what
 * the note taker can hear (#15565, ADR-0134 § 3): a facilitator's or the
 * decider's "leaning", a person confirming the room agreed, and the
 * accountable decider ratifying the call. Each is MANUAL Evidence with a
 * named actor, read by `formalityFromEvidence` (`@/lib/decisions/formality`).
 * Declared here so the read can name them; #15566 writes them.
 */
export const DECISION_LEANING_EVIDENCE_KIND =
  'decision_leaning' as const satisfies FlatEvidenceKind
export const DECISION_AGREED_EVIDENCE_KIND =
  'decision_agreed' as const satisfies FlatEvidenceKind
export const DECISION_RATIFICATION_EVIDENCE_KIND =
  'decision_ratification' as const satisfies FlatEvidenceKind

/**
 * An open decision someone raised (#15568, ADR-0134 § 1): the Evidence
 * behind a `question` Belief, in its raiser's words, at the mandatory
 * classification they chose. Written only by `raiseQuestion`
 * (`@/lib/decisions/questions`). A withdrawal dismisses it with its reason.
 */
export const QUESTION_EVIDENCE_KIND =
  'question' as const satisfies FlatEvidenceKind

/**
 * A change of a question's named decider (#15568, ADR-0134 § 5): MANUAL
 * Evidence on the question whose author is the person who made the change,
 * so "who said this was theirs to decide" has a history. Written only by
 * `setQuestionDecider` (`@/lib/decisions/questions`).
 */
export const QUESTION_DECIDER_EVIDENCE_KIND =
  'question_decider' as const satisfies FlatEvidenceKind

/**
 * A run holding a question out of the room until a date (#15569, ADR-0134
 * § 2): MANUAL Evidence on the question whose author is the person who
 * parked it (`question_parked`), or who referred it to a named person or
 * body with a return date (`question_referred`: `ownerText` names who it
 * went to, `dueText` the return date). The date itself is the question's
 * `Belief.parkedUntil`, which the reads filter on. Written only by
 * `holdQuestion` (`@/lib/decisions/question-holds`).
 */
export const QUESTION_PARKED_EVIDENCE_KIND =
  'question_parked' as const satisfies FlatEvidenceKind
export const QUESTION_REFERRED_EVIDENCE_KIND =
  'question_referred' as const satisfies FlatEvidenceKind

/**
 * A facilitator's Keep or Drop of a "Decisions to make" proposal (#15569,
 * ADR-0134 § 8): MANUAL Evidence on the meeting's run pulse, about the
 * meeting, citing the proposal (`sourceUrls`). A DROP is the row dismissed
 * (`dismissedAt`, reason `dropped`) — the Evidence dismissal fields, reused
 * — and stops re-proposal; a Keep is the row left live. The latest row per
 * proposal wins. Backs no Belief. Written only by
 * `@/lib/decisions/proposal-triage`.
 */
export const DECISION_PROPOSAL_TRIAGE_EVIDENCE_KIND =
  'decision_proposal_triage' as const satisfies FlatEvidenceKind

/**
 * Where a Priority came from, when a legacy row produced it (#15013): the
 * minted-from Evidence (`PriorityEvidence`) of a task's outcome, citing the
 * issue (`/issues/<id>`) or the reflection (`/pulse/<id>`) it was created
 * from. About the Priority (subject `PRIORITY`), backing no Belief. Written
 * by the `tasks_to_outcomes` migration and by the task writers in
 * `src/lib/outcomes/task-outcome-store.ts`.
 */
export const PROVENANCE_EVIDENCE_KIND =
  'provenance' as const satisfies FlatEvidenceKind

/** The family prefix for a logged family communication. */
export const COMMUNICATION_EVIDENCE_PREFIX = 'communication:'

/** The closed member set of the `communication:` family. */
export const COMMUNICATION_EVIDENCE_MEMBERS = ['email', 'phone'] as const

export type CommunicationEvidenceMember =
  (typeof COMMUNICATION_EVIDENCE_MEMBERS)[number]

export type CommunicationEvidenceKind =
  `${typeof COMMUNICATION_EVIDENCE_PREFIX}${CommunicationEvidenceMember}`

export const COMMUNICATION_EMAIL_KIND =
  'communication:email' as const satisfies CommunicationEvidenceKind
export const COMMUNICATION_PHONE_KIND =
  'communication:phone' as const satisfies CommunicationEvidenceKind

/** The family prefix for a staff pulse friction theme; read by prefix. */
export const PULSE_FRICTION_EVIDENCE_PREFIX = 'staff_pulse_friction:'

/**
 * The shape of the friction family's open member: what `frictionThemeSlug`
 * produces from a non-empty theme — lowercase alphanumeric runs joined by
 * single hyphens.
 */
const PULSE_FRICTION_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/

export type PulseFrictionEvidenceKind =
  `${typeof PULSE_FRICTION_EVIDENCE_PREFIX}${string}`

/**
 * The family prefix for a synopsis provider's nightly COUNT row (#15496):
 * messages, meetings, threads — an aggregate with no content of its own.
 * Never linked to a Priority.
 */
export const ACTIVITY_EVIDENCE_PREFIX = 'activity:'

/**
 * The providers that write an `activity:` row. Closed, and deliberately a
 * copy rather than an import of the provider registry
 * (`src/lib/pulse-synopsis/provider-names.ts` is `server-only`; this module
 * is client-safe). `self` and `notes` are absent: those are Cadence-native
 * sources that already write their own Evidence, so the synopsis never
 * duplicates them. `synopsis-evidence-vocabulary-pin.test.ts` pins this list
 * to the registry.
 */
export const ACTIVITY_EVIDENCE_MEMBERS = [
  'calendar',
  'direct_reports',
  'email',
  'github',
  'hubspot',
  'linear',
  'notion',
  'slack',
] as const

export type ActivityEvidenceMember = (typeof ACTIVITY_EVIDENCE_MEMBERS)[number]

export type ActivityEvidenceKind =
  `${typeof ACTIVITY_EVIDENCE_PREFIX}${ActivityEvidenceMember}`

/**
 * The family prefix for a synopsis provider's CONTENT finding (#15496): one
 * thing the person did or met, about the person, from a third-party source.
 */
export const INTEGRATION_EVIDENCE_PREFIX = 'integration:'

/**
 * The providers whose findings have content. A subset of
 * {@link ACTIVITY_EVIDENCE_MEMBERS}: `email` carries metadata only (no
 * subjects, bodies or senders — `GmailWeeklyActivity`), and `direct_reports`
 * is read from the reports' own pulses, which are Cadence-native Evidence
 * already. Both write counts and nothing else.
 */
export const INTEGRATION_EVIDENCE_MEMBERS = [
  'calendar',
  'github',
  'hubspot',
  'linear',
  'notion',
  'slack',
] as const satisfies readonly ActivityEvidenceMember[]

export type IntegrationEvidenceMember =
  (typeof INTEGRATION_EVIDENCE_MEMBERS)[number]

export type IntegrationEvidenceKind =
  `${typeof INTEGRATION_EVIDENCE_PREFIX}${IntegrationEvidenceMember}`

/** Every legal `Evidence.kind`. */
export type EvidenceKind =
  | FlatEvidenceKind
  | CommunicationEvidenceKind
  | PulseFrictionEvidenceKind
  | ActivityEvidenceKind
  | IntegrationEvidenceKind

const flatEvidenceKindSchema = z.enum(FLAT_EVIDENCE_KINDS)

const communicationEvidenceKindSchema = z.templateLiteral([
  COMMUNICATION_EVIDENCE_PREFIX,
  z.enum(COMMUNICATION_EVIDENCE_MEMBERS),
])

const pulseFrictionEvidenceKindSchema = z.templateLiteral([
  PULSE_FRICTION_EVIDENCE_PREFIX,
  z.string().regex(PULSE_FRICTION_SLUG_PATTERN),
])

const activityEvidenceKindSchema = z.templateLiteral([
  ACTIVITY_EVIDENCE_PREFIX,
  z.enum(ACTIVITY_EVIDENCE_MEMBERS),
])

const integrationEvidenceKindSchema = z.templateLiteral([
  INTEGRATION_EVIDENCE_PREFIX,
  z.enum(INTEGRATION_EVIDENCE_MEMBERS),
])

/**
 * The gate. Every writer that cannot be checked by the compiler alone — the
 * canonical triple writer above all — parses through this.
 */
export const evidenceKindSchema: z.ZodType<EvidenceKind> = z.union([
  flatEvidenceKindSchema,
  communicationEvidenceKindSchema,
  pulseFrictionEvidenceKindSchema,
  activityEvidenceKindSchema,
  integrationEvidenceKindSchema,
])

/** The family a kind belongs to: its own value for a flat kind. */
export type EvidenceKindFamily =
  | FlatEvidenceKind
  | 'communication'
  | 'staff_pulse_friction'
  | 'activity'
  | 'integration'

/**
 * Split a stored kind into its family and member, or `null` when it is not in
 * the vocabulary. A flat kind is its own family with a `null` member.
 */
export function parseEvidenceKind(
  value: string,
): { family: EvidenceKindFamily; member: string | null } | null {
  const flat = flatEvidenceKindSchema.safeParse(value)
  if (flat.success) return { family: flat.data, member: null }
  if (communicationEvidenceKindSchema.safeParse(value).success) {
    return {
      family: 'communication',
      member: value.slice(COMMUNICATION_EVIDENCE_PREFIX.length),
    }
  }
  if (pulseFrictionEvidenceKindSchema.safeParse(value).success) {
    return {
      family: 'staff_pulse_friction',
      member: value.slice(PULSE_FRICTION_EVIDENCE_PREFIX.length),
    }
  }
  if (activityEvidenceKindSchema.safeParse(value).success) {
    return {
      family: 'activity',
      member: value.slice(ACTIVITY_EVIDENCE_PREFIX.length),
    }
  }
  if (integrationEvidenceKindSchema.safeParse(value).success) {
    return {
      family: 'integration',
      member: value.slice(INTEGRATION_EVIDENCE_PREFIX.length),
    }
  }
  return null
}

/** The count row's kind for a synopsis provider (#15496). */
export function activityEvidenceKind(
  member: ActivityEvidenceMember,
): ActivityEvidenceKind {
  return `${ACTIVITY_EVIDENCE_PREFIX}${member}`
}

/** A content finding's kind for a synopsis provider (#15496). */
export function integrationEvidenceKind(
  member: IntegrationEvidenceMember,
): IntegrationEvidenceKind {
  return `${INTEGRATION_EVIDENCE_PREFIX}${member}`
}

/**
 * The friction kind for a theme slug (`frictionThemeSlug`). Throws on a slug
 * that would write a kind no prefix reader can name — an empty slug above all.
 */
export function pulseFrictionEvidenceKind(
  slug: string,
): PulseFrictionEvidenceKind {
  return pulseFrictionEvidenceKindSchema.parse(
    `${PULSE_FRICTION_EVIDENCE_PREFIX}${slug}`,
  )
}
